import type { Document } from "@markeroo/shared";

type RegradeReason = NonNullable<Document["needsRegradeReason"]>[0];

/**
 * Format a single regrade reason into a human-readable message.
 */
export function formatRegradeReason(reason: RegradeReason): string {
  const { reason: type, metadata } = reason;

  switch (type) {
    case "criteria_modified":
      return "Assignment criteria changed";

    case "student_ela_grade_changed": {
      const oldGrade = metadata?.old_grade as string | undefined;
      const newGrade = metadata?.new_grade as string | undefined;
      return `Student’s grade level changed from ${oldGrade || "?"} to ${newGrade || "?"}`;
    }

    case "student_accessibility_changed":
      return "Student’s ELL status changed";

    case "assignment_instructions_changed":
      return "Assignment instructions changed";

    case "analysis_mode_changed":
      return "Highlighting mode changed";

    default:
      return "Paper settings changed";
  }
}

/**
 * Aggregate regrade reasons by type and format them for display.
 * Groups multiple criteria modifications into a single message.
 */
export function aggregateRegradeReasons(
  reasons: NonNullable<Document["needsRegradeReason"]>
): string[] {
  if (!reasons || reasons.length === 0) {
    return [];
  }

  // Group by reason type
  const grouped = new Map<string, RegradeReason[]>();

  reasons.forEach((r) => {
    const existing = grouped.get(r.reason) || [];
    grouped.set(r.reason, [...existing, r]);
  });

  // Format each group
  const messages: string[] = [];

  grouped.forEach((group, type) => {
    if (type === "criteria_modified") {
      // Always use simplified message for criteria changes, regardless of count
      messages.push("Assignment criteria changed");
    } else {
      // Use individual formatting for non-aggregatable types
      group.forEach((r) => messages.push(formatRegradeReason(r)));
    }
  });

  return messages;
}
